import React, { useState, useEffect } from 'react'
import { useAppLanguage } from '../../i18n'

type DeleteConfirmModalProps = {
  open: boolean
  title: string
  itemName: string
  itemId: number
  warningMessage: string
  onClose: () => void
  onConfirm: () => Promise<boolean>
}

/**
 * Modal de confirmation de suppression réutilisable
 * Responsabilité unique : confirmation de suppression
 */
export default function DeleteConfirmModal({
  open,
  title,
  itemName,
  itemId,
  warningMessage,
  onClose,
  onConfirm,
}: DeleteConfirmModalProps) {
  const { t } = useAppLanguage()
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset à l'ouverture
  useEffect(() => {
    if (open) {
      setDeleting(false)
      setError(null)
    }
  }, [open])

  // Fermer avec Escape
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !deleting) onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose, deleting])

  const handleConfirm = async () => {
    setDeleting(true)
    setError(null)
    const success = await onConfirm()
    setDeleting(false)
    if (success) {
      onClose()
    } else {
      setError(t.common.errorWhileDeleting)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
        onClick={() => !deleting && onClose()} 
      />
      
      {/* Modal */}
      <div className="absolute inset-x-0 top-1/2 mx-auto w-full max-w-lg -translate-y-1/2 overflow-hidden rounded-3xl border border-red-200/70 bg-white/95 shadow-2xl shadow-red-300/30 dark:border-red-900/60 dark:bg-zinc-950/95">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-red-200/70 bg-red-50/60 px-6 py-4 dark:border-red-900/60 dark:bg-red-950/40">
          <div>
            <h2 className="text-lg font-semibold text-red-700 dark:text-red-200">
              {title}
            </h2>
            <p className="text-sm text-red-600/80 dark:text-red-300/80">
              {t.common.irreversibleAction}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={deleting}
            className="rounded-full border border-red-200/70 px-3 py-1 text-sm text-red-600 transition hover:bg-red-100/60 disabled:opacity-50 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-900/60"
          >
            {t.common.close}
          </button>
        </div>

        {/* Contenu */}
        <div className="space-y-4 px-6 py-6 text-sm text-zinc-700 dark:text-zinc-200">
          <p>
            {t.common.confirmDeletePrefix}{' '}
            <span className="font-semibold text-zinc-900 dark:text-white">
              {itemName}
            </span>
            {' '}
            <span className="text-xs font-medium text-zinc-400 dark:text-zinc-500">
              (ID {itemId})
            </span>{' '}
            ?
          </p>
          <div className="rounded-2xl border border-red-200/70 bg-red-50/70 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
            {warningMessage}
          </div>
          {error && (
            <div className="rounded-2xl border border-red-300 bg-white/90 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200">
              {error}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 border-t border-red-200/60 bg-red-50/50 px-6 py-4 dark:border-red-900/50 dark:bg-red-950/30">
          <button
            onClick={onClose}
            disabled={deleting}
            className="rounded-full border border-zinc-200/70 px-4 py-2 text-sm font-semibold text-zinc-600 transition hover:bg-zinc-100/70 disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800/60"
          >
            {t.common.cancel}
          </button>
          <button
            onClick={handleConfirm}
            disabled={deleting}
            className="rounded-full border border-red-200/70 bg-red-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-600 disabled:opacity-50 dark:border-red-900/60"
          >
            {deleting ? t.common.deleteInProgress : t.common.deletePermanently}
          </button>
        </div>
      </div>
    </div>
  )
}
